/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* ============================================================
   换页幕布的进场 + 首屏内容的错开延迟
   （退场和默认入场都在 theme-custom.css 里，纯 CSS，不依赖这段脚本）
   ============================================================ */
(function () {
  var curtain = document.querySelector('.nxCurtain');

  /* ---- 首屏内容错开 ----
     CSS 已经给这几块挂好了动画，这里只是把 --nxi 逐个写上去让它们错开。
     脚本没跑到的话 --nxi 是 0，大家一起浮起来 —— 层次少一点，
     但绝不会出现「先看见内容、再被隐藏、再动画」的闪烁。 */
  (function stagger() {
    /* querySelector 取第一个 .shopify-section 就对了 —— 不能用 firstElementChild，
       商品页和集合页的 #main 第一个子元素是 div.mobile-toolbar。 */
    var first = document.querySelector('#main > .shopify-section');
    if (!first) return;
    var sel = '.lab,.intro,.sub,.btns,.said,.say,.facts,.srch,.quick';
    /* 封顶 6 档：首屏块数多的页面（All Brands 有九块）不封的话
       最后一块要等到 0.94s，用户已经开始滚了动画才刚出来。 */
    [].slice.call(first.querySelectorAll(sel)).forEach(function (el, i) {
      el.style.setProperty('--nxi', Math.min(i, 6));
    });
  })();

  if (!curtain) return;

  /* ---- 点链接时幕布从左边进来 ----
     只接管「普通的同源左键点击」。下面每一条排除都是有原因的：
       修饰键 / 中键  → 用户想在新标签页开，不能拦
       target=_blank  → 同上
       download / mailto / tel / #锚点 → 根本不是页面跳转
       表单里的按钮   → 不走这条路
     拦错了会弄坏后退按钮和新标签页，比没有过渡糟得多。 */
  var going = false;

  document.addEventListener('click', function (e) {
    if (going) return;
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var a = e.target.closest ? e.target.closest('a[href]') : null;
    if (!a) return;
    if (a.target && a.target !== '_self') return;
    if (a.hasAttribute('download')) return;
    if (a.dataset.noTransition !== undefined) return;

    var href = a.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#') return;
    if (/^(mailto:|tel:|javascript:)/i.test(href)) return;

    var url;
    try { url = new URL(a.href, location.href); } catch (err) { return; }
    if (url.origin !== location.origin) return;
    // 同一个页面内的锚点跳转不算换页
    if (url.pathname === location.pathname && url.search === location.search && url.hash) return;

    e.preventDefault();
    going = true;
    curtain.classList.add('is-in');

    var done = false;
    function go() {
      if (done) return;
      done = true;
      location.href = a.href;
    }
    /* 必须等**最后一条带**落位再跳，不能用 once 抓第一个 animationend ——
       第一条带 0.32s 就结束并冒泡上来，那时屏幕还有一大半没盖住，
       跳过去会露出一条没盖到的缝。
       进场总时长 = 0.045×4（错开）+ 0.32（时长）= 0.50s，兜底给 560ms。 */
    var bands = curtain.querySelectorAll('.nxCurtain__bands i');
    var last = bands[bands.length - 1];
    if (last) last.addEventListener('animationend', go, { once: true });
    setTimeout(go, 560);
  });

  /* 从后退/前进（bfcache）回来时，幕布可能还停在盖住的状态 —— 清掉，
     并让退场动画重新跑一遍。不做这个的话按后退会看到一屏金色。 */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    going = false;
    curtain.classList.remove('is-in');
    curtain.style.animation = 'none';
    void curtain.offsetWidth;          // 强制重排，动画才会重新触发
    curtain.style.animation = '';
  });
})();
